<script setup>
import useMapFilter from "@/composables/useMapFilter";
import moment from "moment/moment";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useSorting} from "@/composables/useSorting";
import forestreeHelpers from "@/helpers/forestreeHelpers";
import BulkAssign from "@/components/home/dashboard/works/BulkAssign.vue";
import WorksProject from "@/components/home/dashboard/works/WorksProject.vue";
import AssignWork from "@/components/AssignWork.vue";
import useDashboardMapFilter from "@/components/home/dashboard/composables/useDashboardMapFilter.js";
import AddressSearch from "@/components/maps/shared/AddressSearch.vue";
import useMapImagery from "@/components/maps/shared/composables/useMapImagery.js";

const props = defineProps({
    works: {
        type: Object,
        required: true
    },
    suburbs: {
        type: Object,
        required: true
    },
    equipment: {
        type: Object,
        required: true
    },
    users: {
        type: Object,
        required: true
    },
    teams: {
        type: Object,
        required: true
    },
    tasks: {
        type: Object,
        required: true
    },
    permissions: {
        type: Object,
        required: true
    },
    unlinkedTrees: {
        type: Object,
        required: true
    },
    refreshData: {
        type: Function,
        required: true
    }
});
// const {map, addTreesGL, selectedFeatures, createMap, polygonWkt} = useMapFilter();

const {
    map,
    osm,
    imageService,
    aerialGroup,
    labels,
    satellite,
    aerial,
    selectedFeatures,
    createMap,
    addTreesGL,
    enablePolygonDraw,
    restart,
    polygonSource,
    clearArea,
    polygonWkt
} = useDashboardMapFilter();

const { baseMapSwitch, addAdditionalLayers } = useMapImagery({
    map,
    osmLayer: osm,
    imageService,
    aerialGroup,
    labelLayer:labels,
    satelliteLayer: satellite,
    aerial,
});


const page = ref(1);
const recentWorks = ref([]);
const selectedUser = ref([]);
const selectedTask = ref([]);
const selectedEquip = ref([]);
const selectedSuburb = ref([]);
const selectedPriority = ref([]);
const showLimit = ref(50);
const filteredLength = ref(null);
const unassignedCount = ref(null);
const assignWork = ref(null);
const assignTypeId = ref(null);
const showAssign = ref(false);
const crms = ref(null);
const showWorkOrder = ref(false);
const selectedWorkOrderURL = ref(null);
const today = ref(moment().format("dddd Do MMM YYYY"));
const showMap = ref(false);
const forMap = ref(null);
const openActions = ref(false);
const showBulkAssign = ref(false);
const showWorksProject = ref(false);
const showUnlinkedTrees = ref(false);

const {sortBy, sortAsc, sortArray} = useSorting("updated_at", true);

const copyText = async (txt) => {
    await navigator.clipboard.writeText(txt);
};
const bulkUpdated = () => {
    props.refreshData();
    showBulkAssign.value = false;
};
const showWO = (woUrl) => {
    selectedWorkOrderURL.value = woUrl;
    showWorkOrder.value = true;
};
const openAssignModal = (work, typeId) => {
    assignWork.value = work;
    assignTypeId.value = typeId;
    showAssign.value = true;
};
const toggleToArray = (userId) => {
    if (selectedUser.value.includes(userId)) {
        selectedUser.value.splice(selectedUser.value.indexOf(userId), 1);
    } else {
        selectedUser.value.push(userId);
    }
};
const updateMap = () => {
    addTreesGL(forMap.value);
};

const close = (e) => {
    if (!this.$refs.actions.contains(e.target)) {
        openActions.value = false;
    }
};
const openActionsMenu = () => {
    openActions.value = !openActions.value;
};
const toggleEquip = (e) => {
    if (!selectedEquip.value.includes(e)) {
        selectedEquip.value.push(e);
    } else {
        selectedEquip.value.splice(selectedEquip.value.indexOf(e), 1);
    }
};
const toggleTasks = (e) => {
    if (!selectedTask.value.includes(e)) {
        selectedTask.value.push(e);
    } else {
        selectedTask.value.splice(selectedTask.value.indexOf(e), 1);
    }
};

const filteredAssignee = computed(() => {
    let filteredList = [...props.works];
    console.log(filteredList)

    if (props.unlinkedTrees.length) {
        props.unlinkedTrees.forEach((tree) => {
            tree.work_orders = tree;
            filteredList.push(tree);
        });
    }

    if (showUnlinkedTrees.value) {
        filteredList = filteredList.filter((work) => {
            return !work.tree;
        });
    }

    if (selectedUser.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (selectedUser.value.includes(-99)) {
                return (
                    work.assignments.length === 0 ||
                    work.assignments.filter((user) => selectedUser.value.includes(user.user_id)).length
                );
            } else {
                return work.assignments.filter((user) => selectedUser.value.includes(user.user_id)).length;
            }
        });
    }

    if (selectedEquip.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.equipment) {
                return work.equipment.filter((e) => selectedEquip.value.includes(e.name)).length;
            }
        });
    }

    if (selectedSuburb.value.length > 0) {
        const lowercaseSuburbs = selectedSuburb.value.map((s) => s.toLowerCase());
        filteredList = filteredList.filter((work) => {
            if (work.tree) {
                return lowercaseSuburbs.includes(work.city.toLowerCase());
            }
        });
    }

    if (selectedTask.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.inspection) {
                return work.inspection.works_as_list.filter((t) => selectedTask.value.includes(t)).length;
            }
        });
    }

    if (selectedPriority.value.length > 0) {
        filteredList = filteredList.filter((work) => {
            if (work.inspection) {
                return selectedPriority.value.includes(work.inspection.priority);
            }
        });
    }

    filteredLength.value = filteredList.length;

    forMap.value = filteredList;
    console.log(filteredList);

    return sortArray(filteredList).slice(0, showLimit.value);
});

const userTotals = computed(() => {
    if (props.users) {
        for (var i = 0; i < props.users.active_users.length; i++) {
            var all = props.works.filter((work) => {
                return work.assignments.filter((user) => user.user_id === props.users.active_users[i].id).length;
            });
            if (all.length) {
                props.users.active_users[i].jobs = all.length;
            }
            var unassigned = props.works.filter((work) => {
                return work.assignments.length === 0;
            });
            if (unassigned.length) {
                unassignedCount.value = unassigned.length;
            } else {
                unassignedCount.value = null;
            }
        }
        return props.users.active_users;
    }
});
const sortList = (col, dir) => {
    if (col === "updated_at") {
        filteredAssignee.value.sort((a, b) => b.max - a.max);
        if (!dir) {
            filteredAssignee.value.sort((a, b) => b.max - a.max).reverse();
        }
    }
};

onMounted(() => {
    createMap(true);
    addAdditionalLayers()
    document.addEventListener("click", close.value);

    addTreesGL(forMap.value);
});

onUnmounted(() => {
    document.removeEventListener("click", close.value);
});

watch([filteredAssignee], () => {
    addTreesGL(forMap.value);
});
</script>

<template>
    <div v-if="page === 1">
        <div class="w-full flex flex-wrap lg:flex-no-wrap">
            <div class="w-full lg:w-2/4">
                <div class="w-full">
                    <p class="my-3 text-2xl text-slate-600">
                        <img src="/images/logos/t1.png" class="h-5 mb-2 -ml-1"/>
                        Work Orders
                        <span v-if="works">({{ works.length }})</span>
                    </p>
                </div>
                <div class="w-full text-md text-slate-500"></div>
            </div>
        </div>

        <div class="w-full flex flex-wrap lg:flex-no-wrap pb-3">
            <div v-if="suburbs.length" class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-2">Priority</p>

                        <v-select
                            class="style-chooser"
                            multiple
                            :searchable="false"
                            v-model="selectedPriority"
                            :options="[
                                { type: 'Low', id: 1 },
                                { type: 'Medium', id: 2 },
                                { type: 'High', id: 3 },
                                { type: 'Extreme', id: 4 },
                            ]"
                            label="type"
                            :reduce="(type) => type.id"
                        />
                    </div>
                </div>
            </div>
            <div v-if="suburbs.length" class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-2">Suburb</p>

                        <v-select
                            class="style-chooser"
                            multiple
                            :searchable="false"
                            v-model="selectedSuburb"
                            :options="suburbs"
                            label="suburb"
                        />
                    </div>
                </div>
            </div>
            <div v-if="tasks && tasks.length" class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-2">Work Task</p>
                        <v-select
                            class="style-chooser"
                            multiple
                            :searchable="false"
                            v-model="selectedTask"
                            :options="tasks"
                            label="task"
                            :reduce="(task) => task.id"
                        />
                    </div>
                </div>
            </div>
            <div class="w-full lg:w-1/4 pr-4">
                <div class="flex justify-end my-3">
                    <div class="w-full items-center">
                        <p class="whitespace-nowrap text-slate-600 text-sm uppercase font-semibold mb-2">
                            Equipment Req.
                        </p>
                        <v-select
                            class="style-chooser"
                            multiple
                            :searchable="false"
                            v-model="selectedEquip"
                            :options="equipment"
                            label="equipment"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="w-full flex flex-wrap lg:flex-no-wrap pb-3">
            <div class="items-center mt-2 pb-1 pt-1">
                <div
                    v-if="!showUnlinkedTrees"
                    @click.prevent="showUnlinkedTrees = true"
                    class="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in"
                >
                    <input
                        type="checkbox"
                        name="toggleUnlinkedTrees"
                        id="toggleUnlinkedTrees"
                        class="toggle-checkbox absolute block w-6 h-6 rounded-full bg-white border-4 border-slate-300 appearance-none cursor-pointer"
                    />
                    <label
                        for="toggleUnlinkedTrees"
                        class="toggle-label block overflow-hidden h-6 rounded-full bg-slate-200 cursor-pointer"
                    ></label>
                </div>
                <div
                    v-else
                    @click.prevent="showUnlinkedTrees = false"
                    class="relative inline-block w-10 mr-2 align-middle select-none transition duration-200 ease-in"
                >
                    <input
                        type="checkbox"
                        name="toggleUnlinkedTrees"
                        id="toggleUnlinkedTrees"
                        class="toggle-checkbox absolute right-0 block w-6 h-6 rounded-full bg-white border-4 border-green-400 appearance-none cursor-pointer"
                    />
                    <label
                        for="toggleUnlinkedTrees"
                        class="toggle-label block overflow-hidden h-6 rounded-full bg-green-400 cursor-pointer"
                    ></label>
                </div>
                <label for="toggleUnlinkedTrees" class="mt-1 text-slate-600 hover:text-blue-600 hover:no-underline"
                >Show Unlinked Works</label
                >
            </div>
        </div>

        <div class="w-full pb-3 text-slate-600 flex justify-between items-end">
            <div>
                Showing <b>{{ filteredAssignee.length }}</b> works of <b>{{ filteredLength }}</b>
                <span v-if="works.length > filteredLength">(filtered)</span>
            </div>
            <div class="flex items-center">
                <div v-show="selectedFeatures.length > 0" class="pr-2 border-r border-slate-300 flex items-center">
                    <div class="pr-2">
                        <strong>{{ selectedFeatures.length }}</strong> Works Selected
                    </div>
                    <div ref="actions">
                        <button
                            @click.prevent="openActionsMenu()"
                            class="mr-2 bg-slate-100 hover:bg-slate-200 font-semibold text-slate-600 p-3 rounded-lg"
                        >
                            Actions
                        </button>

                        <div
                            v-if="openActions"
                            class="relative h-0 left-0"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="menu-button"
                            tabindex="-1"
                        >
                            <div
                                class="mt-1 py-1 z-10 absolute rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none w-56"
                                role="none "
                            >
                                <div
                                    @click="
                                        showBulkAssign = true;
                                        openActions = false;
                                    "
                                    class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                    role="menuitem"
                                    tabindex="-1"
                                    id="menu-item-0"
                                >
                                    Assign Selected Works
                                </div>
                                <div
                                    @click="
                                        showWorksProject = true;
                                        openActions = false;
                                    "
                                    class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                    role="menuitem"
                                    tabindex="-1"
                                    id="menu-item-1"
                                >
                                    Create Project from Selection
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="showMap" class="py-4 px-4 border-r border-slate-300">
                    <button
                        @click.prevent="enablePolygonDraw()"
                        class="mr-2 bg-slate-100 hover:bg-slate-200 font-semibold text-slate-600 p-3 rounded-lg"
                    >
                        Draw Area
                    </button>
                    <button
                        @click.prevent="clearArea()"
                        class="px-4 py-3 bg-slate-100 rounded-lg font-medium cursor-pointer text-md text-blue-700 hover:text-blue-500"
                    >
                        Remove Area
                    </button>
                </div>
                <div class="py-4 pl-4">
                    <button
                        @click.prevent="showMap = !showMap"
                        class="mr-4 font-medium cursor-pointer text-md text-blue-700 hover:text-blue-500 bg-slate-100 p-3 hover:bg-slate-200 rounded-lg"
                    >
                        Toggle Map
                    </button>
                </div>
            </div>
        </div>

        <transition name="slide-down">
            <div :class="showMap ? 'visible map-height' : 'invisible h-0'">
                <div id="map" class="pb-6" style="height: 75vh">
                    <address-search :toggleMap="baseMapSwitch" :map="map" class="relative w-full"></address-search>


                </div>


                <div id="popup" class="ol-popup">
                    <a href="#" id="popup-closer" class="ol-popup-closer"></a>
                    <div id="popup-content" class="text-slate-600"></div>
                </div>
            </div>
        </transition>

        <div class="w-full flex flex-col">
            <div class="-my-2 overflow-x-auto sm:-mx-1 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full">
                    <div class="overflow-x-auto border-b border-slate-200">
                        <div class="min-w-full divide-y divide-slate-200">
                            <div class="bg-slate-100 flex w-full">
                                <div
                                    scope="col"
                                    class="w-1/2 lg:w-1/6 px-2 lg:pl-6 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
                                >
                                    TREE
                                </div>
                                <div
                                    @click.prevent="
                                        sortBy = 'updated_at';
                                        sortAsc = !sortAsc;
                                    "
                                    :class="
                                        sortBy === 'updated_at'
                                            ? 'font-semibold text-blue-600'
                                            : 'font-medium text-slate-600'
                                    "
                                    scope="col"
                                    class="w-1/2 lg:w-1/6 px-2 py-2 text-left text-xs uppercase tracking-wider cursor-pointer"
                                >
                                    LAST UPDATED
                                    <svg
                                        v-show="sortBy === 'updated_at'"
                                        :class="!sortAsc ? 'rotate-180' : ''"
                                        class="inline h-5 w-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div
                                    scope="col"
                                    class="w-1/2 lg:w-3/6 px-2 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
                                >
                                    WORK
                                </div>
                                <div
                                    scope="col"
                                    class="w-1/2 lg:w-1/6 px-2 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider"
                                >
                                    ASSIGN
                                </div>
                                <div
                                    @click.prevent="
                                        sortBy = 'estimated_date';
                                        sortAsc = !sortAsc;
                                        sortList();
                                    "
                                    :class="
                                        sortBy === 'estimated_date'
                                            ? 'font-semibold text-blue-600'
                                            : 'font-medium text-slate-600'
                                    "
                                    scope="col"
                                    class="w-1/2 lg:w-1/6 px-2 py-2 text-left text-xs uppercase tracking-wider cursor-pointer"
                                >
                                    EST. FINISH
                                    <svg
                                        v-show="sortBy === 'estimated_date'"
                                        :class="!sortAsc ? 'rotate-180' : ''"
                                        class="inline h-5 w-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                            clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <transition-group name="slide-up">
                                <div
                                    v-for="work in filteredAssignee"
                                    :key="work.id"
                                    class="bg-white divide-y divide-slate-200 w-full hover:bg-slate-100"
                                >
                                    <div class="flex w-full border-b border-slate-200">
                                        <div class="px-2 lg:pl-6 py-2 w-1/2 lg:w-1/6 whitespace-nowrap">
                                            <div class="flex items-center" v-if="work.tree">
                                                <span
                                                    v-if="work.inspection && work.inspection.priority === 1"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-green-500"
                                                ></span>
                                                <span
                                                    v-if="work.inspection && work.inspection.priority === 2"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-orange-400"
                                                ></span>
                                                <span
                                                    v-if="work.inspection && work.inspection.priority === 3"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-red-600"
                                                ></span>
                                                <div class="ml-4">
                                                    <div class="text-sm font-medium text-slate-800">
                                                        <a
                                                            :href="'/maintenance-map?treeId=' + work.tree.id"
                                                            class="hover:text-blue-700"
                                                        >
                                                            #{{ work.tree.id }}
                                                        </a>
                                                    </div>
                                                    <div class="text-xs text-slate-500">
                                                        {{ work.city }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="flex items-center" v-else>
                                                <span
                                                    v-if="work.forestree_priority === 1"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-green-500"
                                                ></span>
                                                <span
                                                    v-if="work.forestree_priority === 2"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-orange-400"
                                                ></span>
                                                <span
                                                    v-if="work.forestree_priority === 3"
                                                    class="h-3 w-3 rounded-full ring-2 ring-white bg-red-600"
                                                ></span>
                                                <div class="ml-4">
                                                    <div class="text-sm font-medium text-slate-800">
                                                        <a
                                                            :href="'/maintenance-map?woId=' + work.id"
                                                            class="hover:text-blue-700"
                                                        >Unlinked</a
                                                        >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="px-2 py-2 w-1/2 lg:w-1/6 whitespace-nowrap text-xs text-slate-500">
                                            <div class="text-sm font-medium text-slate-600">
                                                {{ forestreeHelpers.dateTime(work.updated_at) }}
                                            </div>
                                            <div v-if="work.inspection" class="text-xs text-slate-500">
                                                {{ work.inspection.name }}
                                            </div>
                                        </div>
                                        <div class="px-2 py-2 w-1/2 lg:w-3/6 whitespace-nowrap">
                                            <div>
                                                <span
                                                    v-if="work.work_orders"
                                                    @click="copyText(work.work_orders.woWorkOrderNumber)"
                                                    class="inline-block cursor-pointer bg-orange-500 rounded-full px-3 text-sm text-orange-100 hover:text-orange-300 mr-1"
                                                >
                                                    {{ work.work_orders.woWorkOrderNumber }}
                                                </span>
                                                <span
                                                    v-if="work.work_orders && work.work_orders.woWorkTypeCode"
                                                    class="inline-block bg-green-500 rounded-full px-3 text-sm text-green-100 mr-1"
                                                >
                                                    {{ work.work_orders.woWorkTypeCode }}
                                                </span>
                                                <div
                                                    v-if="work.work_orders && work.work_orders.woDescription"
                                                    :class="
                                                        work.work_orders.woDescription
                                                            ? 'my-2 px-2 py-1 bg-slate-100 rounded-lg'
                                                            : ''
                                                    "
                                                >
                                                    <span class="inline-block rounded-full text-xs text-slate-600">
                                                        {{ work.work_orders.woDescription }}
                                                    </span>
                                                </div>
                                                <span
                                                    v-if="work.crm"
                                                    class="inline-block bg-red-500 rounded-full px-3 text-sm text-red-100 hover:text-red-300 mr-1"
                                                >
                                                    {{ work.crm.crm_id }}
                                                </span>
                                                <span
                                                    v-for="e in work.equipment"
                                                    :key="e.id"
                                                    @click="toggleEquip(e.name)"
                                                    class="inline-block bg-blue-500 rounded-full px-3 text-sm text-blue-100 hover:text-blue-200 cursor-pointer mr-1"
                                                >
                                                    {{ e.name }}
                                                </span>
                                                <div v-if="work.inspection" class="text-sm text-slate-500 mt-1">
                                                    <span
                                                        v-for="(task, tidx) in work.inspection.works_as_list"
                                                        :key="tidx"
                                                        @click="toggleTasks(task)"
                                                        class="inline-block bg-slate-600 rounded-full px-3 text-sm text-slate-100 hover:text-slate-300 cursor-pointer mr-1"
                                                    >
                                                        {{ forestreeHelpers.getWorkTask(task, tasks) }}
                                                    </span>
                                                </div>
                                                <div v-if="work.inspection" class="mt-1">
                                                    <span
                                                        v-if="work.inspection.work_notes"
                                                        class="inline-block rounded-full text-xs text-slate-500"
                                                    >
                                                        {{ work.inspection.work_notes }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- <div v-else class="px-2 py-2 w-1/2 lg:w-1/6 whitespace-nowrap">
                                            <div>
                                                <div class="text-sm text-slate-900">There are {{tree.works.length}} open works for this tree</div>
                                            </div>
                                        </div> -->
                                        <div class="px-2 py-2 w-1/2 lg:w-1/6 whitespace-nowrap">
                                            <assign-work
                                                class=""
                                                :work="work"
                                                :users="users"
                                                :teams="teams"
                                                assignTypeId="2"
                                                :permissions="permissions"
                                            />
                                        </div>
                                        <div
                                            v-if="work.estimate_finish"
                                            class="px-2 py-2 w-1/2 lg:w-1/6 whitespace-nowrap text-xs text-slate-500"
                                        >
                                            <div class="text-sm font-medium text-slate-600">
                                                {{ forestreeHelpers.justDate(work.estimate_finish) }}
                                            </div>
                                            <div class="text-xs text-slate-500">
                                                {{ forestreeHelpers.daysTo(work.estimate_finish) }}
                                            </div>
                                        </div>
                                        <div v-else class="px-2 py-2 w-1/2 lg:w-1/6 whitespace-nowrap">
                                            <div class="text-xs text-slate-400">NA</div>
                                        </div>
                                    </div>
                                </div>
                            </transition-group>

                            <div v-if="showLimit < filteredLength" class="w-full flex justify-center py-3">
                                <button
                                    @click="showLimit = showLimit + 50"
                                    class="py-2 px-5 bg-slate-200 text-slate-700 font-semibold rounded-lg"
                                >
                                    Show More
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <transition name="slide-out">
            <div
                v-if="showWorkOrder"
                class="fixed inset-0 overflow-hidden"
                aria-labelledby="slide-over-title"
                role="dialog"
                aria-modal="true"
                style="z-index: 100; background-color: #607d8b4d"
            >
                <div class="absolute inset-0 overflow-hidden">
                    <div class="absolute inset-0" aria-hidden="true"></div>

                    <div class="fixed inset-y-0 right-0 pl-10 w-full lg:w-1/2 flex sm:pl-16">
                        <div class="w-full">
                            <div class="h-full flex flex-col pt-6 bg-white shadow-xl overflow-auto">
                                <div class="px-4 sm:px-6">
                                    <div class="flex items-start justify-between">
                                        <h2 class="text-lg font-medium text-slate-900" id="slide-over-title">
                                            TechnologyOne Work Order
                                        </h2>
                                        <div class="ml-3 h-7 flex items-center">
                                            <button
                                                @click="showWorkOrder = false"
                                                class="bg-white rounded-md text-slate-400 hover:text-slate-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            >
                                                <!-- Heroicon name: outline/x -->
                                                <svg
                                                    class="h-6 w-6"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="selectedWorkOrderURL" class="mt-6 relative flex-1">
                                    <iframe class="w-full h-full" :src="selectedWorkOrderURL"></iframe>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <bulk-assign
            v-if="showBulkAssign"
            :selectedFeatures="selectedFeatures"
            :users="users"
            :teams="teams"
            :permissions="permissions"
            :bulk-updated="bulkUpdated"
        />

        <works-project
            v-if="showWorksProject"
            :show-works-project="showWorksProject"
            :polygonWkt="polygonWkt"
            :selectedFeatures="selectedFeatures"
            :users="users"
            :permissions="permissions"
        />
    </div>
</template>

<style>
/* Enter and leave animations can use different */
/* durations and timing functions.              */
.slide-out-enter-active {
    opacity: 1;
    transition: all 0.3s ease;
}

.slide-out-leave-active {
    opacity: 1;
    transition: all 0.3s ease;
}

.slide-out-enter,
.slide-out-leave-to {
    transform: translateX(50vw);
    opacity: 0;
}

.slide-up-enter-active {
    opacity: 1;
    transition: all 0.2s ease;
}

.slide-up-leave-active {
    opacity: 1;
    transition: all 0.2s ease;
}

.slide-up-enter,
.slide-up-leave-to {
    transform: translateY(-50px);
    opacity: 0;
}

.rotate-180 {
    transform: rotate(180deg);
}

.style-chooser .vs__selected {
    background-color: #edf2f7;
    border: 1px solid #dbebfb;
    color: #2b6cb0;
    line-height: 1.7;
}

.style-chooser .vs__search {
    line-height: 1.7;
}

.map-height {
    height: 70vh;
}

.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
    background: #ffffff;
    border: 1px solid #cbd5e0;
    color: #394066;
}

.style-chooser .vs__clear,
.style-chooser .vs__open-indicator {
    fill: #394066;
}
</style>
